import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@/lib/utils';

/*
 * Tabs — DESIGN_SYSTEM.md §2.1
 * Two flavors:
 *   - "pill" (default): bg-surface-2 p-1, active = bg-surface shadow-sm
 *   - "underline": border-bottom 2px transparent → brand-500
 * Optional count chip per trigger via <TabsTrigger.Count>.
 */

type TabsVariant = 'pill' | 'underline';

const TabsContext = React.createContext<{ variant: TabsVariant }>({ variant: 'pill' });

interface TabsRootProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  variant?: TabsVariant;
}

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  TabsRootProps
>(({ variant = 'pill', children, ...props }, ref) => (
  <TabsContext.Provider value={{ variant }}>
    <TabsPrimitive.Root ref={ref} {...props}>
      {children}
    </TabsPrimitive.Root>
  </TabsContext.Provider>
));
Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TabsContext);
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        'inline-flex items-center text-soft',
        variant === 'pill' &&
          'h-10 justify-center rounded-md bg-surface-2 p-1',
        variant === 'underline' &&
          'h-10 gap-6 border-b border-line w-full justify-start',
        className,
      )}
      {...props}
    />
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const { variant } = React.useContext(TabsContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        variant === 'pill' &&
          'rounded-sm px-3 py-1.5 text-sm data-[state=active]:bg-surface data-[state=active]:text-ink data-[state=active]:shadow-sm',
        variant === 'underline' &&
          'h-10 -mb-px border-b-2 border-transparent px-1 text-sm hover:text-ink data-[state=active]:border-brand-500 data-[state=active]:text-ink',
        className,
      )}
      {...props}
    >
      {children}
    </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

/** Count chip used inside <TabsTrigger>: <TabsTrigger>Pending<TabCount>3</TabCount></TabsTrigger> */
const TabCount = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn(
      'inline-flex h-5 min-w-5 items-center justify-center rounded-pill bg-surface-2 px-1.5 text-[11px] font-semibold text-soft',
      className,
    )}
    {...props}
  />
);
TabCount.displayName = 'TabCount';

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent, TabCount };
